import { cva } from "class-variance-authority";
import { XIcon } from "lucide-react";
import { type MouseEvent, useCallback, useMemo } from "react";
import { useTranslation } from "../../i18n";
import { cn } from "../../utils";
import { IconButton } from "../Button/IconButton";
import { type ButtonSize } from "../Button/types";
import { SelectOption } from "../Select/types";
import { Spinner } from "../Spinner";
import { type SpinnerSize } from "../Spinner/types";
import { Tag } from "../Tag";
import type { ComboBoxSize, ComboBoxTriggerProps } from "./types";
import { useComboBoxTrigger } from "./useComboBoxTrigger";

const comboBoxWrapperVariants = cva(
  "box-border flex items-center border border-neutral-100 rounded-xl bg-white",
  {
    variants: {
      size: {
        sm: "min-h-8",
        md: "min-h-10",
        lg: "min-h-12",
      },
      invalid: {
        true: "border-danger-500 outline outline-[2px] outline-danger-500 hover:border-danger-500 hover:outline-danger-500 has-[:focus:not(button)]:border-danger-500 has-[:focus:not(button)]:outline-danger-500 has-[:focus:not(button)]:hover:border-danger-500 has-[:focus:not(button)]:hover:outline-danger-500",
        false:
          "hover:border-neutral-200 hover:outline hover:outline-[2px] hover:outline-neutral-200 has-[:focus:not(button)]:outline has-[:focus:not(button)]:outline-[2px] has-[:focus:not(button)]:outline-primary-500 has-[:focus:not(button)]:hover:outline-primary-500",
      },
      disabled: {
        true: "bg-neutral-100 border-neutral-200 pointer-events-none",
      },
    },
    defaultVariants: {
      invalid: false,
    },
  },
);

const comboBoxInnerVariants = cva(
  "w-full flex flex-wrap py-1.5 pe-1 gap-1.5 items-center",
  {
    variants: {
      size: {
        sm: "min-h-7",
        md: "min-h-9",
        lg: "min-h-11",
      },
    },
  },
);

const comboBoxInputVariants = cva(
  "box-border flex-1 min-w-[120px] outline-none bg-transparent border-0 text-sm placeholder:text-neutral-500/50",
  {
    variants: {
      size: {
        sm: "h-6",
        md: "h-6",
        lg: "h-8",
      },
    },
  },
);

export const ComboBoxMultiTrigger = (props: ComboBoxTriggerProps = {}) => {
  const { t } = useTranslation();

  const {
    disabled,
    size = "md",
    placeholder,
    inputId,
    searchValue,
    setSearchValue,
    onClear,
    showClearButton,
    invalid,
    loading,
    onSelect,
    multiple,
    value,
    open,
    contentId,
    activeDescId,
    onInputKeyDownCapture,
    onInputFocus,
    onInputPaste,
    dataAttributes,
    searchInputRef,
  } = useComboBoxTrigger();

  const buttonSizeMap: Record<ComboBoxSize, ButtonSize> = {
    sm: "xs",
    md: "sm",
    lg: "md",
  };

  const spinnerSizeMap: Record<ComboBoxSize, SpinnerSize> = {
    sm: "2xs",
    md: "xs",
    lg: "xs",
  };

  const spinnerContainerVariants = cva("flex items-center justify-center", {
    variants: {
      size: {
        sm: "w-6 h-6",
        md: "w-8 h-8",
        lg: "w-10 h-10",
      },
    },
  });

  const selectedOptions = useMemo((): SelectOption[] => {
    if (!multiple || !value) {
      return [];
    }

    const valueArray = Array.isArray(value) ? value : [value];
    return valueArray;
  }, [multiple, value]);

  const {
    ref: referenceRef,
    onClick,
    onFocus,
    onKeyDown,
    onMouseDown,
    onPointerDown,
    onPointerEnter,
    id: referenceId,
    className,

    role: _role,
    tabIndex: _tabIndex,
    "aria-expanded": _ariaExpanded,
    "aria-haspopup": _ariaHaspopup,
    "aria-controls": _ariaControls,
    "aria-activedescendant": _ariaActiveDescendant,

    ...restReferenceProps
  } = props;

  const handleTagRemove = useCallback(
    (optionId: string) => {
      const optionToRemove = selectedOptions.find((opt) => opt.id === optionId);
      if (optionToRemove) {
        onSelect(optionToRemove);
        searchInputRef.current?.focus();
      }
    },
    [selectedOptions, onSelect, searchInputRef],
  );

  const handleClear = useCallback(() => {
    onClear?.();
    searchInputRef.current?.focus();
  }, [onClear]);

  const handleClick = useCallback(
    (e: MouseEvent) => {
      onClick?.(e as MouseEvent<HTMLInputElement>);
      searchInputRef.current?.focus();
    },
    [onClick],
  );

  const spinnerContainerClass = spinnerContainerVariants({ size });

  const clearButtonJSX = !disabled && !loading && showClearButton && (
    <IconButton
      variant="ghost"
      color="neutral"
      size={buttonSizeMap[size]}
      onClick={handleClear}
      content={<XIcon />}
      aria-label={t("comboBox.clearSelection")}
    />
  );

  const spinnerJSX = !disabled && loading && (
    <div className={spinnerContainerClass}>
      <Spinner color="primary" size={spinnerSizeMap[size]} />
    </div>
  );

  return (
    <div
      ref={referenceRef}
      id={referenceId}
      className={comboBoxWrapperVariants({
        size,
        invalid: !!invalid,
        disabled: !!disabled,
        className,
      })}
      onClick={handleClick}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      onMouseDown={onMouseDown}
      onPointerDown={onPointerDown}
      onPointerEnter={onPointerEnter}
      {...restReferenceProps}
      role={undefined}
      tabIndex={-1}
      aria-haspopup={undefined}
      aria-expanded={undefined}
      aria-controls={undefined}
      aria-activedescendant={undefined}
      style={{ outlineOffset: "-1px" }}
    >
      <div
        className={cn(
          comboBoxInnerVariants({ size }),
          selectedOptions.length > 0 ? "ps-1.5" : "ps-4",
        )}
      >
        {selectedOptions.map((option) => (
          <Tag
            key={option.id}
            id={option.id}
            label={option.text}
            icon={option.icon}
            size={size}
            className={cn(disabled && "bg-neutral-500/10")}
            textClassName={cn("font-normal", disabled && "text-neutral-500/50")}
            onRemove={!disabled ? () => handleTagRemove(option.id) : undefined}
            disabled={disabled}
          />
        ))}
        <input
          ref={searchInputRef}
          id={inputId}
          {...dataAttributes}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={contentId}
          aria-autocomplete="list"
          aria-activedescendant={activeDescId}
          className={comboBoxInputVariants({ size })}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onFocus={onInputFocus}
          onKeyDownCapture={onInputKeyDownCapture}
          onPaste={onInputPaste}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
        />
      </div>
      <div className="flex items-center self-end pe-[3px] pb-[3px]">
        {spinnerJSX || clearButtonJSX}
      </div>
    </div>
  );
};
